import { useRouter } from 'next/router';
import { Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';
import { mqFrom } from 'styles/styles';
import i18n, { SettingsType } from 'utils/i18n';
import Button from 'components/button';
import Confetti from 'components/confetti';
import Modal from 'components/modal';
import SingleQuestion from 'components-new/single-question';
import MultiQuestion from 'components-new/multi-question';
import OrderQuestion from 'components-new/ordering-question';
import MatchQuestion from 'components-new/matching-question';
import { useForm, useFieldArray } from 'react-hook-form';
import BlanksQuestion from 'components-new/blanks-question';
import ExperiencePointsSvg from 'components/experience-points-svg';

type QuizProps = {
  questionPools: any;
  onComplete: () => void;
  nextUnit: string;
};

interface StyledQuizProps {
  activePool: number;
}

const Quiz: React.FC<QuizProps> = ({ questionPools = [], onComplete, nextUnit }) => {
  const [completed, setCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [noMistakes, setNoMistakes] = useState(false);
  const [showModal, setShowModal] = useState<'Congratulations' | ''>('');
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;
  const [activePool, setActivePool] = useState<number>(0);
  const [answers, setAnswers] = useState([]);
  const [mistakes, setMistakes] = useState(0);

  const questionsCollectionCombined = questionPools
    .reduce((acc, { questionsCollection }) => {
      return acc.concat(questionsCollection.items);
    }, [])
    .filter(
      (question) =>
        question.questionFields.type === 'match' ||
        question.questionFields.type === 'single' ||
        question.questionFields.type === 'multi' ||
        question.questionFields.type === 'fill' ||
        question.questionFields.type === 'order'
    );
  const defaultValues = {
    questions: questionsCollectionCombined.map((question) => {
      switch (question.questionFields.type) {
        case 'match':
          return {
            answers: question.questionFields.answers.map((answer) => ({
              answer: answer.initialOrder,
            })),
          };
        case 'order':
          return {
            answers: question.questionFields.answers.map((_, index) => ({ answer: index })),
          };
        case 'multi':
          return {
            answers: [],
          };
        case 'fill':
          return {
            answers: [],
          };
        default:
          return {
            answers: null,
          };
      }
    }),
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    clearErrors,
    register,
  } = useForm({
    defaultValues: defaultValues,
  });

  const { fields: questionsFields } = useFieldArray({
    control,
    name: 'questions',
  });

  const watchAllFields = watch();

  const onAnswerChange = (answerData: any) => {
    console.log('answerData', answerData);
    setAnswers((prevAnswers) => {
      // Remove the old answer data for this question if it exists
      const filteredAnswers = prevAnswers.filter(
        (answer) => answer['content-question-id'] !== answerData['content-question-id']
      );
      // Add the new answer data
      return [...filteredAnswers, answerData];
    });
  };

  const onSubmit = async (data: any): Promise<void> => {
    const extractQuestion = (name) => {
      const match = name.match(/questions\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    };

    const sortedData = answers.sort((a, b) => {
      const aQuestion = extractQuestion(a.name);
      const bQuestion = extractQuestion(b.name);

      return aQuestion - bQuestion;
    });

    // // Calculating counts
    const correctAnswersCount = sortedData.filter((question) => question.correct).length;
    const wrongAnswersCount = sortedData.length - correctAnswersCount;
    const totalAnswersCount = sortedData.length;

    console.log('submit', {
      sortedData,
      correctAnswersCount,
      totalAnswersCount,
    });

    clearErrors();
    setMistakes(wrongAnswersCount);
    setCompleted(true);
    onComplete();
    setShowModal('Congratulations');

    if (wrongAnswersCount === 0) {
      return setShowConfetti(true);
    }

    return setShowConfetti(false);
  };

  const headline = (mistakes: number): string => {
    let headline;

    switch (true) {
      case mistakes === 0:
        headline = dictionary.wellDone;
        // setShowConfetti(true);
        break;
      case mistakes === 1:
        headline = dictionary.soClose;
        break;
      default:
        headline = dictionary.notQuite;
    }

    return headline;
  };

  return (
    <StyledQuiz activePool={activePool}>
      <form onSubmit={handleSubmit((data: any) => onSubmit(data))}>
        <div className="question-pools">
          <div data-pool={0} className="question-pool">
            {questionsFields.map((field, qIndex: number) => {
              const question = questionsCollectionCombined[qIndex];
              let questionComponent;

              switch (question.questionFields.type) {
                case 'single':
                  questionComponent = (
                    <SingleQuestion
                      answers={question.questionFields.answers}
                      answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                      text={question.questionFields.text}
                      question={question.content.json}
                      questionIndex={qIndex}
                      control={control}
                      completed={completed}
                      image={question.image}
                      onAnswerChange={onAnswerChange}
                      questionId={question.sys.id}
                    />
                  );
                  break;
                case 'multi':
                  questionComponent = (
                    <MultiQuestion
                      answers={question.questionFields.answers}
                      answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                      text={question.questionFields.text}
                      question={question.content.json}
                      questionIndex={qIndex}
                      control={control}
                      completed={completed}
                      image={question.image}
                      onAnswerChange={onAnswerChange}
                      questionId={question.sys.id}
                    />
                  );
                  break;
                case 'match':
                  questionComponent = (
                    <MatchQuestion
                      answers={question.questionFields.answers}
                      answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                      answersDragDrop={question.questionFields?.answersDragDrop}
                      feedback={question.questionFields.feedback}
                      question={question.content.json}
                      questionIndex={qIndex}
                      control={control}
                      completed={completed}
                      image={question.image}
                      register={register}
                      onAnswerChange={onAnswerChange}
                      questionId={question.sys.id}
                    />
                  );
                  break;
                case 'order':
                  questionComponent = (
                    <OrderQuestion
                      answers={question.questionFields.answers}
                      answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                      feedback={question.questionFields.feedback}
                      question={question.content.json}
                      questionIndex={qIndex}
                      control={control}
                      completed={completed}
                      image={question.image}
                      register={register}
                      onAnswerChange={onAnswerChange}
                      questionId={question.sys.id}
                    />
                  );
                  break;
                case 'fill':
                  questionComponent = (
                    <BlanksQuestion
                      answers={question.questionFields.answers}
                      answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                      text={question.questionFields.text}
                      question={question.content.json}
                      questionIndex={qIndex}
                      control={control}
                      completed={completed}
                      feedback={question.questionFields.feedback}
                      image={question.image}
                      onAnswerChange={onAnswerChange}
                      questionId={question.sys.id}
                    />
                  );
                  break;
                default:
                  questionComponent = <>{console.log('notype')}</>;
              }
              return questionComponent;
            })}
          </div>
        </div>
        <div className="buttons-container">
          {!completed && (
            <div className="buttons">
              <Button mode="primary" htmlType="submit">
                {dictionary.quizCheckAnswers}
              </Button>
            </div>
          )}
        </div>
        {errors && errors.questions && (
          <div className="validation-text">{dictionary.quizValidationAllFieldsAreRequired}</div>
        )}
        <Modal
          visible={showModal === 'Congratulations'}
          onClose={() => setShowModal('')}
          headline={headline(mistakes)}
          center
        >
          <p>
            {dictionary.completedKnowledgeCheck} <br />
            {dictionary.beforeContinue}{' '}
            <span className="fake-link" onClick={() => setShowModal('')}>
              {dictionary.quizReviewAnswers}
            </span>
            .
          </p>
          {mistakes === 0 && <ExperiencePointsSvg text={dictionary.completed} short={locale === 'de'} />}
          {nextUnit && (
            <div className="buttons">
              <Button mode="primary" iconName="chevron-right" alignIcon="right" linkProps={{ href: nextUnit }}>
                {dictionary.continue}
              </Button>
            </div>
          )}
        </Modal>
        {showConfetti && <Confetti onComplete={() => setShowConfetti(false)} />}
      </form>
    </StyledQuiz>
  );
};

export const StyledQuiz = styled.ul<StyledQuizProps>`
  margin: 1rem 0;

  .question-pool {
    display: flex;
    flex-direction: column;
    row-gap: 2rem;

    /* &[data-pool]:not([data-pool='${(props) => props.activePool}']) {
      display: none;
    } */
  }

  .buttons-container {
    display: flex;
    column-gap: 1rem;
  }

  .validation-text {
    margin-top: 0.5rem;
    color: var(--danger-base);
    font-weight: 600;
    font-size: 0.875rem;
  }

  .fake-link {
    color: var(--primary-base);
    cursor: pointer;
    text-decoration: none;
    word-wrap: break-word;
    /* text-transform: lowercase; */
  }

  .fake-link:hover {
    color: var(--primary-hover);
    text-decoration: underline;
  }

  .fake-link:visited {
    color: var(--visited);
  }
`;

export default Quiz;
